import type { ReactNode } from "react";
import { ActivityIcon, UsersIcon, ServerIcon, ChartNoAxesCombinedIcon, TicketIcon, MonitorIcon, ListIcon, FlagIcon, CircleUserIcon } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { WorkspaceShell } from "@/components/workspace-shell";
import { cn } from "@/lib/utils";
import { authClient } from "@/lib/transport";
import { useIsAdmin } from "@/lib/admin";
import { setSession, useSession } from "@/lib/session";

const userNav = [
  { to: "/", label: "Worker", icon: ServerIcon, end: true },
  { to: "/devices", label: "Devices", icon: MonitorIcon, end: false },
  { to: "/events", label: "Events", icon: ListIcon, end: false },
  { to: "/account", label: "Account", icon: CircleUserIcon, end: false },
];

const adminNav = [
  { to: "/admin/services", label: "Services", icon: ActivityIcon, end: false },
  { to: "/admin", label: "Accounts", icon: UsersIcon, end: true },
  { to: "/admin/features", label: "Feature rollout", icon: FlagIcon, end: false },
  { to: "/admin/fleet", label: "Fleet", icon: ServerIcon, end: false },
  { to: "/admin/traffic", label: "Traffic", icon: ChartNoAxesCombinedIcon, end: false },
  { to: "/admin/invites", label: "Invites", icon: TicketIcon, end: false },
];

export function AppShell({ children }: { children: ReactNode }) {
  const session = useSession();
  const { isAdmin } = useIsAdmin();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const inAdmin = pathname.startsWith("/admin");
  const adminArea = inAdmin && isAdmin;

  function signOut() {
    const refreshToken = session?.refreshToken;
    setSession(null);
    // The cache is keyed by RPC and input, not by account, so the next sign-in
    // would otherwise be served the previous account's workers from cache
    // before its own request lands.
    queryClient.clear();
    // Local logout is immediate even on a broken link. When reachable, revoke
    // the stable server-side session row too so neither token remains useful.
    if (refreshToken) {
      void authClient.logout({ refreshToken }).catch(() => {
        // The credentials are already gone locally and expire server-side.
      });
    }
  }

  return (
    <WorkspaceShell
      brandHref="/"
      navigationId="console-navigation"
      navigationLabels={{ open: "Toggle navigation", close: "Toggle navigation" }}
      topNavigation={isAdmin && (
        <nav aria-label="Console area" className="flex rounded-lg bg-muted p-1 text-sm">
          <Link to="/" className={cn("rounded-md px-3 py-1.5", !inAdmin ? "bg-background font-medium shadow-sm" : "text-muted-foreground")} aria-current={!inAdmin ? "page" : undefined}>Workspace</Link>
          <Link to="/admin/services" className={cn("rounded-md px-3 py-1.5", inAdmin ? "bg-background font-medium shadow-sm" : "text-muted-foreground")} aria-current={inAdmin ? "page" : undefined}>Admin</Link>
        </nav>
      )}
      headerActions={<>
        <span className="hidden max-w-56 truncate text-xs text-muted-foreground lg:block" data-testid="whoami" title={session?.email}>{session?.email}</span>
        <Button variant="ghost" size="sm" onClick={signOut}>Sign out</Button>
      </>}
      navGroups={[{
        label: adminArea ? "Administration" : "Your workspace",
        ariaLabel: adminArea ? "Admin" : "Main",
        testId: adminArea ? "admin-nav" : undefined,
        items: adminArea ? adminNav : userNav,
      }]}
      sidebarFooter={adminArea ? "Operator view across every account on this control plane." : "Manage your Worker, devices and activity."}
    >
      {children}
    </WorkspaceShell>
  );
}
